/* eslint-disable camelcase */
import React, { FC, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import { useGetList, useNotify, useTranslate } from 'react-admin'
import { themeColor } from '../../layout/theme/color'
import type {
  ApplicationOut,
  ExportRequest,
  McpServerOut,
  WorkflowOut,
} from '../../types/apiRequests'
import { downloadBundle, requestExport } from './api'

type Mode = 'none' | 'all' | 'selected'

interface PickerOption {
  id: string
  name: string
  display_name: string
}

interface SectionPickerProps {
  label: string
  mode: Mode
  setMode: (m: Mode) => void
  options: PickerOption[]
  selected: string[]
  setSelected: (s: string[]) => void
  searchText: string
  onSearchChange: (s: string) => void
  loading: boolean
}

const SectionPicker: FC<SectionPickerProps> = ({
  label,
  mode,
  setMode,
  options,
  selected,
  setSelected,
  searchText,
  onSearchChange,
  loading,
}) => {
  const translate = useTranslate()
  // Compute chips directly from `selected` so removal works even when the item isn't in the current page.
  const value = useMemo(
    () =>
      selected.map((name) =>
        options.find((o) => o.name === name) ?? { id: name, name, display_name: name },
      ),
    [options, selected],
  )
  // Dropdown shows the current API page; off-page selections appear only as chips.
  const dropdownOptions = useMemo(() => {
    const byName = new Map(options.map((o) => [o.name, o]))
    for (const sel of selected) {
      if (!byName.has(sel)) {
        byName.set(sel, { id: sel, name: sel, display_name: sel })
      }
    }
    return Array.from(byName.values())
  }, [options, selected])

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        border: `1px solid ${themeColor.slate10}`,
        bgcolor: themeColor.slate05,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: themeColor.slate90 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.75} role="radiogroup">
          {(['none', 'all', 'selected'] as Mode[]).map((m) => (
            <ModeWrapper key={m} m={m} mode={mode} setMode={setMode} translate={translate} />
          ))}
        </Stack>
      </Stack>
      {mode === 'selected' && (
        <Autocomplete
          multiple
          size="small"
          options={dropdownOptions}
          loading={loading}
          value={value}
          onChange={(_, next) => setSelected(next.map((v) => v.name))}
          inputValue={searchText}
          onInputChange={(_, next, reason) => {
            if (reason !== 'reset') onSearchChange(next)
          }}
          getOptionLabel={(o) => o.display_name || o.name}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          // API does the filtering; local filter is a no-op.
          filterOptions={(opts) => opts}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key: _k, ...tagProps } = getTagProps({ index })
              return (
                <Chip
                  {...tagProps}
                  key={option.name}
                  size="small"
                  label={option.display_name || option.name}
                  onDelete={() => setSelected(selected.filter((n) => n !== option.name))}
                />
              )
            })
          }
          renderInput={(params) => (
            <MuiTextField
              {...params}
              placeholder={loading ? '…' : ''}
              sx={{
                ['& .MuiOutlinedInput-root']: {
                  bgcolor: '#FFFFFF',
                  borderRadius: '8px',
                },
              }}
            />
          )}
        />
      )}
    </Box>
  )
}

const ModeWrapper: FC<{
  m: Mode
  mode: Mode
  setMode: (m: Mode) => void
  translate: (key: string) => string
}> = ({ m, mode, setMode, translate }) => {
  const labelKey = `portalSettings.importExport.export.mode${m.charAt(0).toUpperCase() + m.slice(1)}`
  const active = m === mode
  return (
    <Box
      role="radio"
      aria-checked={active}
      tabIndex={0}
      onClick={() => setMode(m)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setMode(m)
        }
      }}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        px: 1.75,
        py: 0.75,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.1,
        border: `1px solid ${active ? themeColor.blue : themeColor.slate10}`,
        bgcolor: active ? themeColor.blue : 'transparent',
        color: active ? '#FFFFFF' : themeColor.slate60,
        transition: 'all 0.18s ease',
        ['&:hover']: {
          borderColor: active ? themeColor.blue : themeColor.slate20,
          bgcolor: active ? themeColor.blue : themeColor.slate05,
        },
        ['&:focus-visible']: {
          outline: `2px solid ${themeColor.blue60}`,
          outlineOffset: 2,
        },
      }}
    >
      {translate(labelKey)}
    </Box>
  )
}

const ExportDialog: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [appsMode, setAppsMode] = useState<Mode>('all')
  const [serversMode, setServersMode] = useState<Mode>('all')
  const [workflowsMode, setWorkflowsMode] = useState<Mode>('all')
  const [appsSelected, setAppsSelected] = useState<string[]>([])
  const [serversSelected, setServersSelected] = useState<string[]>([])
  const [workflowsSelected, setWorkflowsSelected] = useState<string[]>([])
  const [appsSearch, setAppsSearch] = useState('')
  const [serversSearch, setServersSearch] = useState('')
  const [workflowsSearch, setWorkflowsSearch] = useState('')
  const [includeDeps, setIncludeDeps] = useState(true)
  const [busy, setBusy] = useState(false)

  // Pickers use the API's `q` filter so admins can find entities beyond the first 100 records.
  const { data: apps = [], isFetching: appsLoading } = useGetList<ApplicationOut>(
    'application',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: appsSearch ? { q: appsSearch } : {},
    },
    { enabled: appsMode === 'selected' },
  )
  const { data: servers = [], isFetching: serversLoading } = useGetList<McpServerOut>(
    'mcp_server',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: serversSearch ? { q: serversSearch } : {},
    },
    { enabled: serversMode === 'selected' },
  )
  const { data: workflows = [], isFetching: workflowsLoading } = useGetList<WorkflowOut>(
    'workflow',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: workflowsSearch ? { q: workflowsSearch } : {},
    },
    { enabled: workflowsMode === 'selected' },
  )

  const empty = useMemo(
    () => appsMode === 'none' && serversMode === 'none' && workflowsMode === 'none',
    [appsMode, serversMode, workflowsMode],
  )

  const toRequest = (): ExportRequest => ({
    applications: { mode: appsMode, names: appsMode === 'selected' ? appsSelected : [] },
    mcp_servers: { mode: serversMode, names: serversMode === 'selected' ? serversSelected : [] },
    workflows: {
      mode: workflowsMode,
      names: workflowsMode === 'selected' ? workflowsSelected : [],
    },
    include_dependencies: includeDeps,
  })

  const handleExport = async () => {
    setBusy(true)
    try {
      const bundle = await requestExport(toRequest())
      downloadBundle(bundle)
      onClose()
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Export failed', { type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontWeight: 600,
          color: themeColor.slate90,
          borderBottom: `1px solid ${themeColor.slate10}`,
        }}
      >
        {translate('portalSettings.importExport.export.modalTitle')}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: themeColor.slate60, mb: 3 }}>
          {translate('portalSettings.importExport.export.modalIntro')}
        </Typography>
        <Stack spacing={2}>
          <SectionPicker
            label={translate('portalSettings.importExport.export.applications')}
            mode={appsMode}
            setMode={setAppsMode}
            options={apps.map((a) => ({
              id: String(a.id),
              name: a.name,
              display_name: a.display_name,
            }))}
            selected={appsSelected}
            setSelected={setAppsSelected}
            searchText={appsSearch}
            onSearchChange={setAppsSearch}
            loading={appsLoading}
          />
          <SectionPicker
            label={translate('portalSettings.importExport.export.mcpServers')}
            mode={serversMode}
            setMode={setServersMode}
            options={servers.map((s) => ({
              id: String(s.id),
              name: s.name,
              display_name: s.display_name,
            }))}
            selected={serversSelected}
            setSelected={setServersSelected}
            searchText={serversSearch}
            onSearchChange={setServersSearch}
            loading={serversLoading}
          />
          <SectionPicker
            label={translate('portalSettings.importExport.export.workflows')}
            mode={workflowsMode}
            setMode={setWorkflowsMode}
            options={workflows.map((w) => ({
              id: String(w.id),
              name: w.name,
              display_name: w.display_name,
            }))}
            selected={workflowsSelected}
            setSelected={setWorkflowsSelected}
            searchText={workflowsSearch}
            onSearchChange={setWorkflowsSearch}
            loading={workflowsLoading}
          />
          <Box
            sx={{
              mt: 1,
              pt: 1.5,
              pb: 0.5,
              borderTop: `1px solid ${themeColor.slate10}`,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={includeDeps}
                  onChange={(_, v) => setIncludeDeps(v)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: themeColor.slate80 }}>
                  {translate('portalSettings.importExport.export.includeDependencies')}
                </Typography>
              }
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          borderTop: `1px solid ${themeColor.slate10}`,
        }}
      >
        <Button onClick={onClose} disabled={busy}>
          {translate('portalSettings.importExport.export.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudDownloadOutlinedIcon />}
          onClick={() => {
            void handleExport()
          }}
          disabled={empty || busy}
        >
          {translate('portalSettings.importExport.export.downloadButton')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExportDialog
